import { Injectable } from "@angular/core";
import { FormControl, FormBuilder, FormGroup } from "@angular/forms";
import { compile } from "@hsgovtech/hsutils";
import { AuthService } from "./auth.service";
import { ClientService } from "./client.service";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";
import { ValidatorService } from "./validator.service";
import { DateService } from "./date.service";

@Injectable({
  providedIn: "root",
})
export class FormService extends BaseService {
  formMap: any = {};

  subscriptionMap = new Map();

  constructor(
    private authService: AuthService,
    private clientService: ClientService,
    private dateService: DateService,
    private formBuilder: FormBuilder,
    private validatorService: ValidatorService,
    logService: LogService,
  ) {
    super(logService, "FormService");
  }

  public getDefaultsSync(): any {
    const { password, ...user } = this.authService.currentAuth();
    const config = this.clientService.getCurrentClientConfig();
    return {
      now: this.dateService.formatDate(),
      userID: user.userID,
      auth: user,
      config,
    };
  }

  public async getDefaults(): Promise<any> {
    const { password, ...user } = await this.authService.getAuth();
    const config = this.clientService.getCurrentClientConfig();
    return {
      now: this.dateService.formatDate(),
      userID: user.userID,
      auth: user,
      config,
    };
  }

  public isFormValid(formName: string) {
    return this.formMap[formName].state.valid;
  }

  public addFieldListener(formGroup: FormGroup, field: string, listener: any) {
    const subscription = formGroup
      .get(field)
      ?.valueChanges.subscribe((value) => {
        listener(value);
      });
    if (subscription) {
      this.addSubscription(formGroup, subscription);
    }
  }

  public populateDefaults(key: string, form: any, data: any) {
    const defaults = this.getDefaultsSync();
    const record: any = { ...data };

    const fieldData = form.reduce((acc: any, ff: any) => {
      const defaultsCopy = { ...defaults };
      const fieldDefinition = { ...ff };
      if (fieldDefinition.type === "time") {
        defaultsCopy.now = this.dateService.formatTime();
      }

      if (
        (record[fieldDefinition.properties.id] === undefined ||
          record[fieldDefinition.properties.id] === "") &&
        fieldDefinition.default
      ) {
        return {
          ...acc,
          [fieldDefinition.properties.id]: compile(
            fieldDefinition.default || "",
            {
              ...defaultsCopy,
              ...data,
            },
          ),
        };
      }
      return acc;
    }, {});
    this.updateValues(key, fieldData);
  }

  public reset(formName: string) {
    const form = this.getForm(formName);
    this.clearSubscriptions(form.state);
    this.subscriptionMap
      .get(form.state)
      ?.forEach((subscription: any) => subscription.unsubscribe());
    this.subscriptionMap.set(form.state, []);
    form.state.reset();
    this.populateDefaults(formName, form.fields, {});
    return this.formMap;
  }

  public addSubscription(subscriptionGroupIdentifier: any, subscription: any) {
    if (this.subscriptionMap.get(subscriptionGroupIdentifier)) {
      this.subscriptionMap.set(subscriptionGroupIdentifier, [
        ...this.subscriptionMap.get(subscriptionGroupIdentifier),
        subscription,
      ]);
    } else {
      this.subscriptionMap.set(subscriptionGroupIdentifier, [subscription]);
    }
  }

  public clearSubscriptions(subscriptionGroupIdentifier: any) {
    this.subscriptionMap
      .get(subscriptionGroupIdentifier)
      ?.forEach((subscription: any) => subscription.unsubscribe());
    this.subscriptionMap.set(subscriptionGroupIdentifier, []);
  }

  public resetAll() {
    [...this.subscriptionMap.values()]
      .flat()
      .forEach((subscription: any) => subscription.unsubscribe());
    this.subscriptionMap = new Map();
    this.formMap = {};
    return this.formMap;
  }

  public getForm(key: string): {
    fields: any;
    state: FormGroup<{ [x: string]: FormControl }>;
  } {
    return this.formMap[key];
  }

  public getFormValues(key: string): any {
    return this.formMap[key]?.state?.getRawValue?.() || {};
  }

  public setForm(
    key: string,
    fields: any,
    state: FormGroup<{ [x: string]: FormControl }>,
  ) {
    this.formMap[key] = { fields, state };
  }

  public updateValues(key: string, newValue: any) {
    const form = this.formMap[key];
    if (form) {
      form.state.patchValue(newValue);
    }
  }

  public clearValues(key: string) {
    const form = this.formMap[key];
    if (form) {
      form.state.reset();
    }
  }

  public parseValuesToCompare(value: any, desiredValue: any) {
    const parsedValue = parseFloat(value);
    const parsedDesiredValue = parseFloat(desiredValue);
    const valueToCompare = !Number.isNaN(parsedValue) ? parsedValue : 0;
    const desiredValueToCompare = !Number.isNaN(parsedDesiredValue)
      ? parsedDesiredValue
      : 0;
    return [valueToCompare, desiredValueToCompare];
  }

  public checkLogicCondition(
    value: string,
    desiredValue: string,
    valueAction: string,
  ): boolean {
    if (valueAction === "!==") {
      return value !== desiredValue;
    }
    if (valueAction === "===") {
      return value === desiredValue;
    }
    if (valueAction === "indexOf") {
      return desiredValue.includes(",")
        ? desiredValue.split(",").includes(value)
        : desiredValue.includes(value);
    }
    if (valueAction === "notIndexOf") {
      return desiredValue.includes(",")
        ? !desiredValue.split(",").includes(value)
        : !desiredValue.includes(value);
    }
    if (valueAction === "either") {
      return desiredValue.includes(",")
        ? desiredValue.split(",").includes(value)
        : desiredValue.includes(value);
    }
    if (valueAction === "neither") {
      return desiredValue.includes(",")
        ? !desiredValue.split(",").includes(value)
        : !desiredValue.includes(value);
    }
    const [valueToCompare, desiredValueToCompare] = this.parseValuesToCompare(
      value,
      desiredValue,
    );
    if (valueAction === ">") {
      return valueToCompare > desiredValueToCompare;
    }
    if (valueAction === ">=") {
      return valueToCompare >= desiredValueToCompare;
    }
    if (valueAction === "<") {
      return valueToCompare < desiredValueToCompare;
    }
    if (valueAction === "<=") {
      return valueToCompare <= desiredValueToCompare;
    }
    return false;
  }

  public getSrcValue(key: string, primary: FormGroup, secondary?: any) {
    const secondaryIsFormGroup = secondary instanceof FormGroup;
    const primaryValue = primary.get(key);
    if (!primaryValue && secondary) {
      return secondaryIsFormGroup ? secondary.get(key) : secondary[key];
    }
    return primaryValue ? primaryValue.value : undefined;
  }

  public handleFieldLogic(form: any[], formGroup: FormGroup, data: any) {
    form.forEach((item: any) =>
      this.iterateOverLogic(item.properties.id, item.logic, formGroup, data),
    );
  }

  public applyFieldLogic(item: any, formGroup: any, fieldName: string) {
    const fc: any = formGroup.get(fieldName);
    if (item.type === "show") {
      fc.isHidden = false;
      fc.enable();
    }
    if (item.type === "hide") {
      fc.isHidden = true;
      fc.disable();
    }
    if (item.type === "change") {
      fc.setValue(item.targetValue);
    }
  }

  public iterateOverLogic(
    fieldName: string,
    logic: any[],
    formGroup: FormGroup,
    secondarySrc?: any,
  ) {
    if (logic && Array.isArray(logic)) {
      logic.forEach((item) => {
        const srcValue = this.getSrcValue(item.field, formGroup, secondarySrc);
        if (srcValue === undefined) {
          return;
        }
        if (
          this.checkLogicCondition(
            srcValue,
            item.realValue || item.value,
            item.valueAction,
          )
        ) {
          this.applyFieldLogic(item, formGroup, fieldName);
        }
        // if there is a form control for the field then subscribe to changes
        const formControl = formGroup.get(item.field);
        if (formControl) {
          const subscription = formControl.valueChanges.subscribe(
            (value: any) => {
              if (
                this.checkLogicCondition(
                  value,
                  item.realValue || item.value,
                  item.valueAction,
                )
              ) {
                this.applyFieldLogic(item, formGroup, fieldName);
              }
            },
          );
          this.addSubscription(formGroup, subscription);
        }
      });
    }
  }

  public removePlaceHolderSelectOptions(form: any) {
    return form.reduce((acc: any, item: any) => {
      if (item.type === "select" && !item.options) {
        this.logger.warn({ message: "Select field has no options", item });
      } else if (item.type === "select" && item.options) {
        const selectOptions = item.options.filter((option: any) => {
          return option.value !== "";
        });
        return [...acc, { ...item, options: selectOptions }];
      }
      return [...acc, item];
    }, []);
  }

  public async generateForm(
    key: string,
    form: any[],
    data: any,
    additionalData: any = {},
    options: any = {},
  ): Promise<FormGroup<{ [x: string]: FormControl }>> {
    const defaults = await this.getDefaults();
    const record: any = { ...data };

    const fieldData = form.reduce((acc: any, ff: any) => {
      const defaultsCopy = { ...defaults };
      const fieldDefinition = { ...ff };
      if (fieldDefinition.type === "time") {
        defaultsCopy.now = this.dateService.formatTime();
      }

      if (
        (record[fieldDefinition.properties.id] === undefined ||
          record[fieldDefinition.properties.id] === "") &&
        fieldDefinition.default
      ) {
        record[fieldDefinition.properties.id] = compile(
          fieldDefinition.default || "",
          {
            ...defaultsCopy,
            ...additionalData,
          },
        );
      }

      const validators =
        this.validatorService.getFieldValidators(fieldDefinition);

      const fc = new FormControl(
        record[fieldDefinition.properties.id] !== undefined
          ? record[fieldDefinition.properties.id]
          : "",
        validators,
      );
      if (
        this.validatorService.isFieldRequired(fieldDefinition) &&
        !fieldDefinition.properties.label.includes("(required)")
      ) {
        fieldDefinition.properties.label = `${fieldDefinition.properties.label} (required)`;
      }

      if (key === "locationInfo" && !fieldDefinition.editOnTouch) {
        fc.disable();
      }

      if (
        fieldDefinition.type === "signature" &&
        record[fieldDefinition.properties.id]
      ) {
        fieldDefinition.isSigned = true;
        fieldDefinition.signatureValue = record[fieldDefinition.properties.id];
      }

      if (fieldDefinition.properties.hideField) {
        (fc as any).isHidden = true;
        fc.disable();
      }

      return { ...acc, [fieldDefinition.properties.id]: fc };
    }, {});

    const formValidators = form.reduce((acc: any, formField: any) => {
      const validators = this.validatorService.getFormValidators(
        formField,
        options.validatorFieldAccessors,
      );
      return [...acc, ...validators];
    }, []);

    const formGroup = this.formBuilder.group(
      {
        ...fieldData,
        ...additionalData,
      },
      { validators: formValidators, updateOn: "change" },
    );

    if (options.onChange) {
      const subscription = formGroup.valueChanges.subscribe((value) => {
        if (!formGroup.disabled) {
          options.onChange(value);
        }
      });
      this.addSubscription(formGroup, subscription);
    }

    if (options.listeners) {
      Object.entries(options.listeners).forEach(
        ([listenerName, listener]: any) => {
          const subscription = formGroup
            .get(listenerName)
            ?.valueChanges.subscribe((value) => {
              listener(value);
            });
          if (subscription) {
            this.addSubscription(formGroup, subscription);
          }
        },
      );
    }
    const newForm = this.removePlaceHolderSelectOptions(form);
    this.handleFieldLogic(newForm, formGroup, data);
    this.setForm(key, newForm, formGroup);
    return formGroup;
  }
}
